import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { Search, ExternalLink, MoreHorizontal, ArrowRightLeft, X, ShoppingBag, Pencil, Archive, ArchiveRestore } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { useServerPagination } from '@/hooks/useServerPagination';
import { ListPagination } from '@/components/ListPagination';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';
import { AssignPartnerSheet } from './AssignPartnerSheet';
import { RecordSaleSheet, type RecordSaleForm, type SaleInitialData } from './RecordSaleSheet';
import { toast } from 'sonner';
import VehiclePipeline, { VehiclePipelineStages, type PipelineStage, type PipelineGroup } from './VehiclePipeline';
import {
    type VehicleStage,
    PROCUREMENT_STAGE_KEYS,
    BOOKED_AND_BEYOND_STAGES,
    CONTRACT_STAGE_KEYS,
    PROCUREMENT_STAGE_CONFIG,
    CONTRACT_STAGE_CONFIG,
    STAGE_BADGE_STYLES,
    STAGE_LABELS,
    computeVehicleStage,
    fetchVehicleContractsMap,
} from './vehicleStages';
import { VehicleCifDisplay, VehicleValueDisplay } from './VehicleFinancialCells';
import { formatShortDate } from './ProcurementContainers';
import { BUSINESS_TZ } from '@/lib/format';

interface VehiclesRecordsProps {
    /** Lock the view to a specific group. When set, the group tabs are hidden. */
    group?: PipelineGroup;
    /** Rendered right-aligned in the search + plugs row (e.g. a view-toggle control from the parent page). */
    controlsExtra?: ReactNode;
}

const ITEMS_PER_PAGE = 15;

// ─── Helpers ────────────────────────────────────────────────────────────────

function DriverAvatar({ driver }: { driver: { first_name: string; last_name: string; photo: string | null } }) {
    const initials = `${driver.first_name[0] || ''}${driver.last_name[0] || ''}`;
    return (
        <Avatar className="h-7 w-7 ring-2 ring-background">
            <SignedAvatarImage src={driver.photo} alt={`${driver.first_name} ${driver.last_name}`} />
            <AvatarFallback className="text-[10px] bg-muted">{initials}</AvatarFallback>
        </Avatar>
    );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function VehiclesRecords({ group, controlsExtra }: VehiclesRecordsProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [engineSizeFilter, setEngineSizeFilter] = useState<'all' | '3-plug' | '4-plug'>('all');

    // Partner assignment state
    const [assignPartnerOpen, setAssignPartnerOpen] = useState(false);
    const [partnerAssignTarget, setPartnerAssignTarget] = useState<{
        id: number;
        label: string;
        currentPartnerId: number | null;
    } | null>(null);
    const [unassignPartnerTarget, setUnassignPartnerTarget] = useState<{
        id: number;
        partnerName: string;
    } | null>(null);

    // Archive vehicle state (Unarchive is direct — no confirmation target needed)
    const [archiveTarget, setArchiveTarget] = useState<{ id: number; label: string } | null>(null);

    // Sell vehicle state
    const [sellTarget, setSellTarget] = useState<{ id: number; label: string } | null>(null);
    const [editSaleTarget, setEditSaleTarget] = useState<{ vehicleId: number; sale: SaleInitialData } | null>(null);

    const [stageFilter, setStageFilter] = useState<VehicleStage | null>(null); // null = All within group
    const [groupFilter, setGroupFilter] = useState<PipelineGroup>(group ?? 'all');

    // When driven by an external group prop, keep groupFilter in sync
    useEffect(() => {
        if (group) setGroupFilter(group);
    }, [group]);


    const pagination = useServerPagination({ totalCount: 0, itemsPerPage: ITEMS_PER_PAGE });

    const getSortColumn = (key: string | null): string | null => {
        const nonSortableColumns = ['vehicleName', 'status', 'registrationNumber'];
        if (key && nonSortableColumns.includes(key)) return null;
        return key || 'created_at';
    };

    const { sortKey, sortDirection, handleSort } = useTableSort({
        defaultSortKey: 'created_at',
        defaultSortDirection: 'desc',
        onSortChange: pagination.resetPage,
    });

    // ── Fetch contract map (status + drivers per vehicle) ──
    const { data: vehicleContractMap } = useQuery({
        queryKey: ['vehicle-contracts-map'],
        queryFn: fetchVehicleContractsMap,
    });

    // ── Fetch ALL fleet vehicles with transactions (for stage computation) ──
    const { data: allFleetVehicles } = useQuery({
        queryKey: ['fleet-vehicles-stages'],
        queryFn: async () => {
            const q = supabase
                .from('vehicles')
                .select('id, engine_size, archived_at, vehicle_procurement(est_arrival_at, ship_date, ship_name, voyage_no), vehicle_transactions(transaction_type)');
            const { data, error } = await (q as any);
            if (error) throw error;
            return data as { id: number; engine_size: number | null; archived_at: string | null; vehicle_procurement: { est_arrival_at: string | null; ship_date: string | null; ship_name: string | null; voyage_no: string | null } | null; vehicle_transactions: { transaction_type: string }[] }[];
        },
    });

    // ── Fetch vehicle sales (for sold stage detection + buyer names + sale price + tooltip breakdown) ──
    interface SaleMapEntry {
        saleId: number;
        buyerId: number;
        buyerName: string;
        buyerPhone: string;
        buyerAddress: string;
        salePrice: number | null;
        expectedAmount: number;
        currency: string;
        notes: string;
        saleDate: string;
        transactions: { id: number; amount: number; currency: string; transaction_at: string; description: string | null }[];
    }
    const { data: vehicleSalesMap } = useQuery({
        queryKey: ['vehicle-sales-map'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicle_sales')
                .select('id, vehicle_id, expected_amount, notes, created_at, vehicle_sale_transactions(transaction_id, transactions(id, amount, currency, transaction_at, description)), buyers(id, name, phone, address)');
            if (error) throw error;
            const map = new Map<number, SaleMapEntry>();
            (data || []).forEach((s: any) => {
                const txns = (s.vehicle_sale_transactions || []).map((vst: any) => ({
                    id: vst.transactions?.id ?? vst.transaction_id,
                    amount: vst.transactions?.amount || 0,
                    currency: vst.transactions?.currency || 'GHS',
                    transaction_at: vst.transactions?.transaction_at || '',
                    description: vst.transactions?.description || null,
                }));
                const totalReceived = txns.reduce((sum: number, t: any) => sum + t.amount, 0);
                // Sale date = first payment date; a sale with no dated payments falls back to
                // when it was recorded so it can't sink to the bottom of the Sold list.
                const firstTxnAt = txns.reduce((earliest: string, t: { transaction_at: string }) => (
                    t.transaction_at && (!earliest || new Date(t.transaction_at) < new Date(earliest)) ? t.transaction_at : earliest
                ), '');
                map.set(s.vehicle_id as number, {
                    saleId: s.id,
                    buyerId: s.buyers?.id ?? 0,
                    buyerName: s.buyers?.name || 'Unknown',
                    buyerPhone: s.buyers?.phone || '',
                    buyerAddress: s.buyers?.address || '',
                    salePrice: totalReceived || null,
                    expectedAmount: s.expected_amount ?? 0,
                    currency: txns[0]?.currency || 'GHS',
                    notes: s.notes || '',
                    saleDate: firstTxnAt || s.created_at,
                    transactions: txns,
                });
            });
            return map;
        },
    });

    // ── Compute stage per vehicle + pipeline counts ──
    const { vehicleStageMap, vehicleProcMap, stageCounts, stageFilteredIds } = useMemo(() => {
        const stageMap = new Map<number, VehicleStage | null>();
        const procMap = new Map<number, { est_arrival_at: string | null }>();
        // Derived from the stage configs so a newly added stage can never be missed here
        // (a missing key made counts[stage]++ produce NaN, rendering that chip as empty).
        const counts = Object.fromEntries(
            [...PROCUREMENT_STAGE_CONFIG, ...CONTRACT_STAGE_CONFIG].map(s => [s.key, 0])
        ) as Record<VehicleStage, number>;

        // Helper: does a vehicle pass the engine size filter?
        const passesEngineFilter = (v: any): boolean => {
            if (engineSizeFilter === 'all') return true;
            const cc = v.engine_size;
            if (!cc || cc <= 0) return false;
            if (engineSizeFilter === '3-plug') return cc <= 1000;
            if (engineSizeFilter === '4-plug') return cc > 1000;
            return true;
        };

        if (allFleetVehicles && vehicleContractMap) {
            allFleetVehicles.forEach((v: any) => {
                // Store procurement data for table access
                procMap.set(v.id, { est_arrival_at: v.vehicle_procurement?.est_arrival_at ?? null });

                // Compute stage for every vehicle (needed for badge display)
                let stage: VehicleStage | null = null;
                if (vehicleSalesMap?.has(v.id)) {
                    stage = 'sold';
                } else {
                    const txTypes = (v.vehicle_transactions || []).map((vt: any) => vt.transaction_type);
                    const contractStatus = vehicleContractMap.statusMap.get(v.id);
                    const proc = v.vehicle_procurement;
                    const hasShippingDetails = !!(proc?.ship_name || proc?.voyage_no);
                    stage = computeVehicleStage(txTypes, contractStatus, proc?.est_arrival_at ?? null, proc?.ship_date ?? null, hasShippingDetails, v.archived_at ?? null);
                }
                stageMap.set(v.id, stage);

                // Only count vehicles that pass the engine filter
                if (stage && passesEngineFilter(v)) counts[stage]++;
            });
        }

        // Get IDs for the current filter (stage + engine size)
        let filteredIds: number[] | null = null;
        const engineActive = engineSizeFilter !== 'all';

        if (stageFilter !== null) {
            // Specific stage selected
            filteredIds = [];
            stageMap.forEach((stage, vehicleId) => {
                if (stage === stageFilter) filteredIds!.push(vehicleId);
            });
        } else if (groupFilter !== 'all') {
            // Group tab selected, no specific stage — show all in group
            const groupKeys = groupFilter === 'procurement' ? PROCUREMENT_STAGE_KEYS : CONTRACT_STAGE_KEYS;
            filteredIds = [];
            stageMap.forEach((stage, vehicleId) => {
                if (stage && groupKeys.includes(stage)) filteredIds!.push(vehicleId);
            });
        } else if (engineActive) {
            // No stage/group filter but engine filter is active — need IDs to pass to query
            // (the actual engine filtering is done server-side, so pass null to avoid double-filtering)
            filteredIds = null;
        }

        return { vehicleStageMap: stageMap, vehicleProcMap: procMap, stageCounts: counts, stageFilteredIds: filteredIds };
    }, [allFleetVehicles, vehicleContractMap, vehicleSalesMap, stageFilter, groupFilter, engineSizeFilter]);

    // ── Pipeline stages for the grouped tabs ──
    const procurementStages: PipelineStage[] = PROCUREMENT_STAGE_CONFIG.map(s => ({
        ...s, count: stageCounts[s.key] || 0,
    }));
    const contractStages: PipelineStage[] = CONTRACT_STAGE_CONFIG.map(s => ({
        ...s, count: stageCounts[s.key] || 0,
    }));

    const totalFleetCount = Object.values(stageCounts).reduce((sum, c) => sum + c, 0);
    const procurementCount = PROCUREMENT_STAGE_KEYS.reduce((sum, key) => sum + (stageCounts[key] || 0), 0);
    const contractCount = CONTRACT_STAGE_KEYS.reduce((sum, key) => sum + (stageCounts[key] || 0), 0);

    // ── Count query ──
    const { data: countData } = useQuery({
        queryKey: ['vehicles-count', debouncedSearch, stageFilter, stageFilteredIds, engineSizeFilter],
        queryFn: async () => {
            if (stageFilteredIds !== null && stageFilteredIds.length === 0) return 0;

            let searchMatchIds: number[] | null = null;
            if (debouncedSearch) {
                const [regResult, procResult] = await Promise.all([
                    supabase
                        .from('vehicles')
                        .select('id, vehicle_registrations!inner(number)')
                        .ilike('vehicle_registrations.number', `%${debouncedSearch}%`),
                    supabase
                        .from('vehicle_procurement')
                        .select('vehicle_id')
                        .ilike('listing_url', `%${debouncedSearch}%`),
                ]);
                const regIds = regResult.data?.map(v => v.id) || [];
                const procIds = procResult.data?.map((v: any) => v.vehicle_id) || [];
                searchMatchIds = [...new Set([...regIds, ...procIds])];
            }

            let query = supabase.from('vehicles').select('*', { count: 'exact', head: true });

            if (debouncedSearch) {
                if (searchMatchIds && searchMatchIds.length > 0) {
                    query = query.or(`make.ilike.%${debouncedSearch}%,model.ilike.%${debouncedSearch}%,vin.ilike.%${debouncedSearch}%,color.ilike.%${debouncedSearch}%,id.in.(${searchMatchIds.join(',')})`);
                } else {
                    query = query.or(`make.ilike.%${debouncedSearch}%,model.ilike.%${debouncedSearch}%,vin.ilike.%${debouncedSearch}%,color.ilike.%${debouncedSearch}%`);
                }
            }

            if (stageFilteredIds !== null) query = query.in('id', stageFilteredIds);
            if (engineSizeFilter === '3-plug') query = query.lte('engine_size', 1000);
            if (engineSizeFilter === '4-plug') query = query.gt('engine_size', 1000);

            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
        enabled: allFleetVehicles !== undefined && vehicleContractMap !== undefined,
        placeholderData: keepPreviousData,
    });

    const paginationWithCount = useServerPagination({ totalCount: countData || 0, itemsPerPage: ITEMS_PER_PAGE });

    // ── Stage-date ordering (Terminated / Sold) ─────────────────────────────
    // Both stages read most-recent-first by when they ended — dates the vehicles table can't sort on.
    // Unless the user has taken over the sort, the whole (small) stage is fetched and paged client-side.
    const stageDateOrdered = (stageFilter === 'terminated' || stageFilter === 'sold') && getSortColumn(sortKey) === 'created_at';

    // ── Main vehicles query ──
    const { data: vehicles, isLoading } = useQuery({
        queryKey: ['vehicles', stageDateOrdered ? 'all' : paginationWithCount.currentPage, sortKey, sortDirection, debouncedSearch, stageFilter, stageFilteredIds, engineSizeFilter],
        queryFn: async () => {
            if (stageFilteredIds !== null && stageFilteredIds.length === 0) return [];

            let searchMatchIds: number[] | null = null;
            if (debouncedSearch) {
                const [regResult, procResult] = await Promise.all([
                    supabase
                        .from('vehicles')
                        .select('id, vehicle_registrations!inner(number)')
                        .ilike('vehicle_registrations.number', `%${debouncedSearch}%`),
                    supabase
                        .from('vehicle_procurement')
                        .select('vehicle_id')
                        .ilike('listing_url', `%${debouncedSearch}%`),
                ]);
                const regIds = regResult.data?.map(v => v.id) || [];
                const procIds = procResult.data?.map((v: any) => v.vehicle_id) || [];
                searchMatchIds = [...new Set([...regIds, ...procIds])];
            }

            const sortColumn = getSortColumn(sortKey);
            let query = supabase.from('vehicles').select(`*, vehicle_procurement(listing_url, cif), vehicle_registrations(number), vehicle_transactions(transaction_type, allocated_amount, allocated_fee, transactions(amount, fee, currency)), partners!vehicles_partner_id_fkey(name)`);

            if (sortColumn) query = query.order(sortColumn, { ascending: sortDirection === 'asc' });
            else query = query.order('created_at', { ascending: false });

            if (debouncedSearch) {
                if (searchMatchIds && searchMatchIds.length > 0) {
                    query = query.or(`make.ilike.%${debouncedSearch}%,model.ilike.%${debouncedSearch}%,vin.ilike.%${debouncedSearch}%,color.ilike.%${debouncedSearch}%,id.in.(${searchMatchIds.join(',')})`);
                } else {
                    query = query.or(`make.ilike.%${debouncedSearch}%,model.ilike.%${debouncedSearch}%,vin.ilike.%${debouncedSearch}%,color.ilike.%${debouncedSearch}%`);
                }
            }

            if (stageFilteredIds !== null) query = query.in('id', stageFilteredIds);
            if (engineSizeFilter === '3-plug') query = query.lte('engine_size', 1000);
            if (engineSizeFilter === '4-plug') query = query.gt('engine_size', 1000);
            if (!stageDateOrdered) query = query.range(paginationWithCount.from, paginationWithCount.to);

            const { data, error } = await query;
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined && allFleetVehicles !== undefined && vehicleContractMap !== undefined,
        placeholderData: keepPreviousData,
    });

    const isFetching = vehicles === undefined && countData !== undefined;

    useEffect(() => { paginationWithCount.resetPage(); }, [debouncedSearch, stageFilter, groupFilter, engineSizeFilter]);

    const formatCurrency = (amount: number, currency: string = 'GHS') => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

    // ── Partner assignment helpers ───────────────────────────────────────────

    const getVehicleLabel = (vehicle: any) => {
        const name = `${vehicle.make} ${vehicle.model}`;
        const url = vehicle.vehicle_procurement?.listing_url;
        const stockId = url ? url.replace(/\/+$/, '').split('/').pop() : null;
        return stockId ? `${name} · ${stockId}` : name;
    };

    const handleAssignPartner = (vehicle: any) => {
        setPartnerAssignTarget({
            id: vehicle.id,
            label: getVehicleLabel(vehicle),
            currentPartnerId: vehicle.partner_id ?? null,
        });
        setAssignPartnerOpen(true);
    };

    const handleReassignPartner = (vehicle: any) => {
        handleAssignPartner(vehicle);
    };

    const handleUnassignPartner = (vehicle: any) => {
        setUnassignPartnerTarget({
            id: vehicle.id,
            partnerName: vehicle.partners?.name || 'Unknown',
        });
    };

    const unassignPartnerMutation = useMutation({
        mutationFn: async (vehicleId: number) => {
            const { error } = await supabase
                .from('vehicles')
                .update({ partner_id: null })
                .eq('id', vehicleId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Partner removed from vehicle');
            queryClient.invalidateQueries({ queryKey: ['vehicles'] });
            queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
            queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicles'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicles-page'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicle-stats'] });
            queryClient.invalidateQueries({ queryKey: ['partner-financial-summary'] });
            queryClient.invalidateQueries({ queryKey: ['partner-recent-activity'] });
            queryClient.invalidateQueries({ queryKey: ['partners'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-vehicles-for-partner'] });
            queryClient.invalidateQueries({ queryKey: ['procurement-containers-vehicles'] });
            setUnassignPartnerTarget(null);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const handleConfirmUnassign = () => {
        if (unassignPartnerTarget) {
            unassignPartnerMutation.mutate(unassignPartnerTarget.id);
        }
    };

    const handlePartnerSheetClose = (open: boolean) => {
        setAssignPartnerOpen(open);
        if (!open) {
            setPartnerAssignTarget(null);
        }
    };

    // ── Archive vehicle helpers ──────────────────────────────────────────────
    // Marks a vehicle as never reaching a contract (third-party sale, third-party procurement, or
    // internal Primelane use). Archive requires confirmation; Unarchive is its direct undo.

    const handleArchiveVehicle = (vehicle: { id: number; make: string; model: string; vehicle_procurement?: { listing_url?: string | null } | null }) => {
        setArchiveTarget({ id: vehicle.id, label: getVehicleLabel(vehicle) });
    };

    const handleUnarchiveVehicle = (vehicle: { id: number }) => {
        unarchiveVehicleMutation.mutate(vehicle.id);
    };

    const invalidateArchiveQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
        queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });
        queryClient.invalidateQueries({ queryKey: ['procurement-containers-vehicles'] });
    };

    const archiveVehicleMutation = useMutation({
        mutationFn: async (vehicleId: number) => {
            const { error } = await supabase
                .from('vehicles')
                .update({ archived_at: new Date().toISOString() })
                .eq('id', vehicleId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Vehicle archived');
            invalidateArchiveQueries();
            setArchiveTarget(null);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const unarchiveVehicleMutation = useMutation({
        mutationFn: async (vehicleId: number) => {
            const { error } = await supabase
                .from('vehicles')
                .update({ archived_at: null })
                .eq('id', vehicleId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Vehicle unarchived');
            invalidateArchiveQueries();
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const handleConfirmArchive = () => {
        if (archiveTarget) {
            archiveVehicleMutation.mutate(archiveTarget.id);
        }
    };

    // ── Sell vehicle helpers ─────────────────────────────────────────────────

    const handleSellVehicle = (vehicle: any) => {
        setSellTarget({ id: vehicle.id, label: getVehicleLabel(vehicle) });
    };

    const sellVehicleMutation = useMutation({
        mutationFn: async ({ vehicleId, form }: { vehicleId: number; form: RecordSaleForm }) => {
            // 1. Create buyer
            const { data: buyer, error: buyerError } = await supabase
                .from('buyers')
                .insert({ name: form.buyerName, phone: form.buyerPhone || null, address: form.buyerAddress || null })
                .select('id')
                .single();
            if (buyerError) throw buyerError;

            // 2. Create vehicle sale with expected amount and notes
            const { data: sale, error: saleError } = await supabase
                .from('vehicle_sales')
                .insert({
                    vehicle_id: vehicleId,
                    buyer_id: buyer.id,
                    expected_amount: form.expectedAmount,
                    notes: form.notes || null,
                })
                .select('id')
                .single();
            if (saleError) throw saleError;

            // 3. Link transactions via junction table
            if (form.transactionIds.length > 0) {
                const { error: junctionError } = await supabase
                    .from('vehicle_sale_transactions')
                    .insert(
                        form.transactionIds.map(txnId => ({
                            vehicle_sale_id: sale.id,
                            transaction_id: txnId,
                        }))
                    );
                if (junctionError) throw junctionError;
            }
        },
        onSuccess: () => {
            toast.success('Vehicle sale recorded');
            queryClient.invalidateQueries({ queryKey: ['vehicle-sales-map'] });
            queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });
            queryClient.invalidateQueries({ queryKey: ['vehicle-contracts-map'] });
            queryClient.invalidateQueries({ queryKey: ['vehicles'] });
            queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
            queryClient.invalidateQueries({ queryKey: ['unlinked-credit-transactions'] });
            setSellTarget(null);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    // ── Edit sale mutation ──────────────────────────────────────────────────
    const editSaleMutation = useMutation({
        mutationFn: async ({ saleId, buyerId, form }: { saleId: number; buyerId: number; form: RecordSaleForm }) => {
            // 1. Update buyer
            const { error: buyerError } = await supabase
                .from('buyers')
                .update({ name: form.buyerName, phone: form.buyerPhone || null, address: form.buyerAddress || null })
                .eq('id', buyerId);
            if (buyerError) throw buyerError;

            // 2. Update vehicle sale expected_amount + notes
            const { error: saleError } = await supabase
                .from('vehicle_sales')
                .update({ expected_amount: form.expectedAmount, notes: form.notes || null })
                .eq('id', saleId);
            if (saleError) throw saleError;

            // 3. Replace junction table rows: delete all then re-insert
            const { error: deleteError } = await supabase
                .from('vehicle_sale_transactions')
                .delete()
                .eq('vehicle_sale_id', saleId);
            if (deleteError) throw deleteError;

            if (form.transactionIds.length > 0) {
                const { error: insertError } = await supabase
                    .from('vehicle_sale_transactions')
                    .insert(
                        form.transactionIds.map(txnId => ({
                            vehicle_sale_id: saleId,
                            transaction_id: txnId,
                        }))
                    );
                if (insertError) throw insertError;
            }
        },
        onSuccess: () => {
            toast.success('Sale updated');
            queryClient.invalidateQueries({ queryKey: ['vehicle-sales-map'] });
            queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });
            queryClient.invalidateQueries({ queryKey: ['unlinked-credit-transactions'] });
            setEditSaleTarget(null);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const getColorValue = (color: string): string => {
        const trimmed = color.trim();
        if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed}`;
        return trimmed;
    };

    const getStatusBadge = (vehicleId: number) => {
        const stage = vehicleStageMap.get(vehicleId);
        if (!stage) return <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-400">—</Badge>;
        return <Badge variant="outline" className={STAGE_BADGE_STYLES[stage]}>{STAGE_LABELS[stage]}</Badge>;
    };

    // Whether to show the Est. Arrival column (Booked and beyond stages)
    const showEstArrival = stageFilter !== null
        ? BOOKED_AND_BEYOND_STAGES.includes(stageFilter)
        : groupFilter === 'procurement';

    // Terminated/Sold arrive unpaged (see stageDateOrdered) — order by stage date, then cut the page.
    const displayVehicles = useMemo(() => {
        const rows = vehicles ?? [];
        if (!stageDateOrdered) return rows;
        const stageDate = (vehicleId: number) => stageFilter === 'terminated'
            ? vehicleContractMap?.terminationDateMap?.get(vehicleId)
            : vehicleSalesMap?.get(vehicleId)?.saleDate;
        return [...rows]
            .sort((a, b) => {
                const aDate = stageDate(a.id);
                const bDate = stageDate(b.id);
                // Vehicles with no stage date sink to the bottom; equals keeps the server order.
                if (!aDate || !bDate) return aDate ? -1 : bDate ? 1 : 0;
                return new Date(bDate).getTime() - new Date(aDate).getTime();
            })
            .slice(paginationWithCount.from, paginationWithCount.to + 1);
    }, [vehicles, stageDateOrdered, stageFilter, vehicleContractMap, vehicleSalesMap, paginationWithCount.from, paginationWithCount.to]);

    return (
        <Card className="border-0">
            <CardContent className="space-y-4">
                {/* Search + view toggle + Pipeline stepper */}
                <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="Search vehicles..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                        </div>
                        <Select
                            value={engineSizeFilter}
                            onValueChange={(val) => setEngineSizeFilter(val as 'all' | '3-plug' | '4-plug')}
                        >
                            <SelectTrigger className="w-[140px] shrink-0">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Plugs</SelectItem>
                                <SelectItem value="3-plug">3 Plugs</SelectItem>
                                <SelectItem value="4-plug">4 Plugs</SelectItem>
                            </SelectContent>
                        </Select>
                        {controlsExtra && <div className="ml-auto">{controlsExtra}</div>}
                    </div>
                    {!group && (
                        <VehiclePipeline
                            procurementStages={procurementStages}
                            contractStages={contractStages}
                            activeGroup={groupFilter}
                            activeStage={stageFilter}
                            onGroupChange={(g) => setGroupFilter(g)}
                            onStageClick={(key) => setStageFilter(key as VehicleStage | null)}
                            totalCount={totalFleetCount}
                            procurementCount={procurementCount}
                            contractCount={contractCount}
                        />
                    )}
                    {group && group !== 'all' && (
                        <VehiclePipelineStages
                            stages={group === 'procurement' ? procurementStages : contractStages}
                            activeStage={stageFilter}
                            onStageClick={(key) => setStageFilter(key as VehicleStage | null)}
                            groupCount={group === 'procurement' ? procurementCount : contractCount}
                        />
                    )}
                </div>

                {isLoading && !vehicles ? (
                    <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
                ) : (
                    <div className={isFetching ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'}>
                        <ListPagination currentPage={paginationWithCount.currentPage} totalPages={paginationWithCount.totalPages} totalItems={paginationWithCount.totalItems} onPageChange={paginationWithCount.goToPage} />
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <SortableTableHead>Vehicle</SortableTableHead>
                                        <SortableTableHead>Identification</SortableTableHead>
                                        <SortableTableHead>Partner</SortableTableHead>
                                        <SortableTableHead>Status</SortableTableHead>
                                        {showEstArrival && <SortableTableHead>Est. Arrival</SortableTableHead>}
                                        <SortableTableHead>CIF</SortableTableHead>
                                        <SortableTableHead>{stageFilter === 'sold' ? 'Buyer' : ['terminated', 'draft', 'active', 'completed'].includes(stageFilter || '') ? 'Drivers' : ''}</SortableTableHead>
                                        {stageFilter === 'sold' && <SortableTableHead>Drivers</SortableTableHead>}
                                        <SortableTableHead sortKey="amount" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} align="right">{stageFilter === 'sold' ? 'Sale Price' : 'Value'}</SortableTableHead>
                                        <SortableTableHead className="w-12"></SortableTableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {displayVehicles.map((vehicle) => (
                                        <TableRow key={vehicle.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/dashboard/vehicles/${vehicle.id}`)}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    {(() => {
                                                        const listingUrl = (vehicle as any).vehicle_procurement?.listing_url || '';
                                                        const sbtMatch = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
                                                        if (sbtMatch) {
                                                            const cacheBust = vehicle.updated_at ? `?v=${new Date(vehicle.updated_at).getTime()}` : '';
                                                            const photoUrl = `https://img.sbtjapan.com/img/carphoto/${sbtMatch[1]}/normal/f.jpg${cacheBust}`;
                                                            return (
                                                                <HoverCard openDelay={200} closeDelay={100}>
                                                                    <HoverCardTrigger asChild>
                                                                        <img src={photoUrl} alt="" className="h-9 w-9 rounded-lg border border-gray-200 object-cover flex-shrink-0 cursor-zoom-in" />
                                                                    </HoverCardTrigger>
                                                                    <HoverCardContent side="right" align="center" className="w-auto p-1" sideOffset={8}>
                                                                        <img src={photoUrl} alt={`${vehicle.make} ${vehicle.model}`} className="w-[480px] rounded-md" />
                                                                    </HoverCardContent>
                                                                </HoverCard>
                                                            );
                                                        }
                                                        return <div className="h-9 w-9 rounded-lg border border-gray-200 flex-shrink-0" style={{ backgroundColor: getColorValue(vehicle.color) || '#e5e7eb' }} />;
                                                    })()}
                                                    <div>
                                                        <p className="font-medium">{vehicle.make} {vehicle.model}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {vehicle.manufactured_at || ''}
                                                            {(vehicle as any).engine_size ? `${vehicle.manufactured_at ? ' · ' : ''}${(vehicle as any).engine_size}cc` : ''}
                                                        </p>
                                                    </div>
                                                    {(vehicle as any).vehicle_procurement?.listing_url && (
                                                        <a
                                                            href={(vehicle as any).vehicle_procurement?.listing_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={e => e.stopPropagation()}
                                                            className="ml-auto text-muted-foreground/50 hover:text-primary transition-colors"
                                                            title="View listing"
                                                        >
                                                            <ExternalLink className="h-3.5 w-3.5" />
                                                        </a>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    {vehicle.vehicle_registrations?.number && (
                                                        <p className="font-medium">{vehicle.vehicle_registrations.number}</p>
                                                    )}
                                                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{vehicle.vin}</code>
                                                    {(() => {
                                                        const url = (vehicle as any).vehicle_procurement?.listing_url;
                                                        if (!url) return null;
                                                        const stockId = url.replace(/\/+$/, '').split('/').pop();
                                                        if (!stockId) return null;
                                                        return <p className="text-xs text-muted-foreground mt-0.5">{stockId}</p>;
                                                    })()}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {(vehicle as any).partners?.name ? <span className="text-sm">{(vehicle as any).partners.name}</span> : <span className="text-muted-foreground">—</span>}
                                            </TableCell>
                                            <TableCell>{getStatusBadge(vehicle.id)}</TableCell>
                                            {showEstArrival && (
                                                <TableCell>
                                                    {(() => {
                                                        const proc = vehicleProcMap.get(vehicle.id);
                                                        const stage = vehicleStageMap.get(vehicle.id);
                                                        if (!proc?.est_arrival_at || !stage || !BOOKED_AND_BEYOND_STAGES.includes(stage)) {
                                                            return <span className="text-muted-foreground">—</span>;
                                                        }
                                                        return (
                                                            <span className="text-sm">
                                                                {formatShortDate(proc.est_arrival_at)}
                                                            </span>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <VehicleCifDisplay cif={(vehicle as any).vehicle_procurement?.cif} vehicleTransactions={(vehicle as any).vehicle_transactions} />
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const vehicleStage = vehicleStageMap?.get(vehicle.id);
                                                    const driverStages = ['terminated', 'draft', 'active', 'completed'];
                                                    const isSold = vehicleStage === 'sold' || stageFilter === 'sold';
                                                    if (isSold) {
                                                        const sale = vehicleSalesMap?.get(vehicle.id);
                                                        return sale ? <span className="text-sm">{sale.buyerName}</span> : <span className="text-muted-foreground">—</span>;
                                                    }
                                                    const showDrivers = driverStages.includes(stageFilter || '') || (!stageFilter && driverStages.includes(vehicleStage || ''));
                                                    if (!showDrivers) return null;
                                                    const drivers = vehicleContractMap?.driverMap?.get(vehicle.id);
                                                    if (!drivers || drivers.length === 0) return <span className="text-muted-foreground">—</span>;
                                                    const latest = drivers[0];
                                                    return (
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex -space-x-2">
                                                                {[...drivers].reverse().map((d, i) => (
                                                                    <DriverAvatar key={i} driver={d} />
                                                                ))}
                                                            </div>
                                                            <span className="text-sm">{latest.first_name} {latest.last_name}</span>
                                                        </div>
                                                    );
                                                })()}
                                            </TableCell>
                                            {stageFilter === 'sold' && (
                                                <TableCell>
                                                    {(() => {
                                                        const drivers = vehicleContractMap?.driverMap?.get(vehicle.id);
                                                        if (!drivers || drivers.length === 0) return <span className="text-muted-foreground">—</span>;
                                                        return (
                                                            <div className="flex items-center gap-2">
                                                                <div className="flex -space-x-2">
                                                                    {[...drivers].reverse().map((d, i) => (
                                                                        <DriverAvatar key={i} driver={d} />
                                                                    ))}
                                                                </div>
                                                                <span className="text-sm">{drivers[0].first_name} {drivers[0].last_name}</span>
                                                            </div>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}
                                            <TableCell className="text-right font-medium">{(() => {
                                                // For sold vehicles, show the sale price with tooltip breakdown
                                                const vehicleStage = vehicleStageMap?.get(vehicle.id);
                                                const isSold = vehicleStage === 'sold' || stageFilter === 'sold';
                                                if (isSold) {
                                                    const sale = vehicleSalesMap?.get(vehicle.id);
                                                    if (sale?.salePrice) {
                                                        const outstanding = sale.expectedAmount - sale.salePrice;
                                                        return (
                                                            <TooltipProvider delayDuration={200}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <span className="cursor-default border-b border-dashed border-muted-foreground/30">
                                                                            {formatCurrency(sale.salePrice, sale.currency)}
                                                                        </span>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent side="left" className="p-0">
                                                                        <div className="min-w-[220px] text-xs">
                                                                            {sale.transactions.map((txn, idx) => (
                                                                                <div key={idx} className="flex justify-between gap-6 px-3 py-1.5 border-b border-border/50">
                                                                                    <span className="text-muted-foreground">
                                                                                        {txn.transaction_at
                                                                                            ? new Date(txn.transaction_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: BUSINESS_TZ })
                                                                                            : 'Payment'}
                                                                                    </span>
                                                                                    <span className="font-mono font-medium">{formatCurrency(txn.amount, txn.currency)}</span>
                                                                                </div>
                                                                            ))}
                                                                            <div className="flex justify-between gap-6 px-3 py-1.5 border-b border-border/50 bg-muted/30">
                                                                                <span className="text-muted-foreground">Expected</span>
                                                                                <span className="font-mono font-medium">{formatCurrency(sale.expectedAmount, sale.currency)}</span>
                                                                            </div>
                                                                            <div className={`flex justify-between gap-6 px-3 py-1.5 font-semibold ${outstanding > 0 ? 'text-red-500' : 'text-green-600 dark:text-green-400'}`}>
                                                                                <span>Outstanding</span>
                                                                                <span className="font-mono">
                                                                                    {formatCurrency(Math.abs(outstanding), sale.currency)}
                                                                                    {outstanding <= 0 && ' ✓'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        );
                                                    }
                                                    return <span className="text-muted-foreground">—</span>;
                                                }

                                                return <VehicleValueDisplay vehicleTransactions={(vehicle as any).vehicle_transactions} />;
                                            })()}</TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                {groupFilter === 'contracts' ? (
                                                    // Contracts view: show menu for terminated and sold vehicles
                                                    (() => {
                                                        const vStage = vehicleStageMap.get(vehicle.id);
                                                        const isSoldV = vStage === 'sold';
                                                        const isTerminated = vStage === 'terminated';
                                                        if (!isSoldV && !isTerminated) return null;
                                                        return (
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                                                        <MoreHorizontal className="h-4 w-4" />
                                                                        <span className="sr-only">Actions</span>
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    {isTerminated && (
                                                                        <DropdownMenuItem onClick={() => handleSellVehicle(vehicle)}>
                                                                            <ShoppingBag className="h-4 w-4 mr-2" />
                                                                            Sell
                                                                        </DropdownMenuItem>
                                                                    )}
                                                                    {isSoldV && (() => {
                                                                        const saleData = vehicleSalesMap?.get(vehicle.id);
                                                                        if (!saleData) return null;
                                                                        return (
                                                                            <DropdownMenuItem onClick={() => {
                                                                                setEditSaleTarget({
                                                                                    vehicleId: vehicle.id,
                                                                                    sale: {
                                                                                        saleId: saleData.saleId,
                                                                                        buyerId: saleData.buyerId,
                                                                                        buyerName: saleData.buyerName,
                                                                                        buyerPhone: saleData.buyerPhone,
                                                                                        buyerAddress: saleData.buyerAddress,
                                                                                        expectedAmount: saleData.expectedAmount,
                                                                                        notes: saleData.notes,
                                                                                        transactions: saleData.transactions.map(t => ({ ...t, type: 'CREDIT' })),
                                                                                    },
                                                                                });
                                                                            }}>
                                                                                <Pencil className="h-4 w-4 mr-2" />
                                                                                Edit Sale
                                                                            </DropdownMenuItem>
                                                                        );
                                                                    })()}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        );
                                                    })()
                                                ) : (
                                                    // Procurement / All view: partner assign/reassign/unassign + archive/unarchive
                                                    (() => {
                                                        const isArchived = !!(vehicle as { archived_at: string | null }).archived_at;
                                                        const archiveMenuItem = isArchived ? (
                                                            <DropdownMenuItem onClick={() => handleUnarchiveVehicle(vehicle)}>
                                                                <ArchiveRestore className="h-4 w-4 mr-2" />
                                                                Unarchive
                                                            </DropdownMenuItem>
                                                        ) : (
                                                            <DropdownMenuItem onClick={() => handleArchiveVehicle(vehicle)}>
                                                                <Archive className="h-4 w-4 mr-2" />
                                                                Archive
                                                            </DropdownMenuItem>
                                                        );

                                                        return !(vehicle as any).partner_id ? (
                                                            <div className="flex items-center justify-end gap-1">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleAssignPartner(vehicle)}
                                                                >
                                                                    Assign
                                                                </Button>
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                                            <MoreHorizontal className="h-4 w-4" />
                                                                            <span className="sr-only">Actions</span>
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end">
                                                                        {archiveMenuItem}
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            </div>
                                                        ) : (
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                                                        <MoreHorizontal className="h-4 w-4" />
                                                                        <span className="sr-only">Actions</span>
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem onClick={() => handleReassignPartner(vehicle)}>
                                                                        <ArrowRightLeft className="h-4 w-4 mr-2" />
                                                                        Reassign
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onClick={() => handleUnassignPartner(vehicle)}
                                                                        className="text-destructive focus:text-destructive"
                                                                    >
                                                                        <X className="h-4 w-4 mr-2" />
                                                                        Unassign
                                                                    </DropdownMenuItem>
                                                                    {archiveMenuItem}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        );
                                                    })()
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {displayVehicles.length === 0 && !isLoading && (
                                        <TableRow>
                                            <TableCell colSpan={(stageFilter === 'sold' ? 9 : 8) + (showEstArrival ? 1 : 0)} className="h-32 text-center text-muted-foreground">
                                                {search || stageFilter !== null ? 'No vehicles match your filters' : 'No vehicles found'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                )}
            </CardContent>

            {/* Assign/Reassign Partner Sheet */}
            <AssignPartnerSheet
                open={assignPartnerOpen}
                onOpenChange={handlePartnerSheetClose}
                vehicleId={partnerAssignTarget?.id ?? null}
                vehicleLabel={partnerAssignTarget?.label ?? ''}
                currentPartnerId={partnerAssignTarget?.currentPartnerId ?? null}
            />

            {/* Unassign Partner Confirmation */}
            <AlertDialog open={!!unassignPartnerTarget} onOpenChange={(open) => !open && setUnassignPartnerTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove Partner?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove <strong>{unassignPartnerTarget?.partnerName}</strong> from this vehicle.
                            The vehicle will appear as unassigned.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmUnassign}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Archive Vehicle Confirmation */}
            <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Archive vehicle?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This marks <strong>{archiveTarget?.label}</strong> as archived — for vehicles that won't
                            reach a contract (sold to a third party, procured for a third party, or kept for internal
                            Primelane use). You can undo this anytime via Unarchive.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmArchive}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Archive
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Record Sale Sheet (create mode) */}
            <RecordSaleSheet
                open={!!sellTarget}
                onOpenChange={(open) => { if (!open) setSellTarget(null); }}
                onSubmit={(form) => {
                    if (sellTarget) {
                        sellVehicleMutation.mutate({ vehicleId: sellTarget.id, form });
                    }
                }}
                isPending={sellVehicleMutation.isPending}
            />

            {/* Edit Sale Sheet */}
            <RecordSaleSheet
                open={!!editSaleTarget}
                onOpenChange={(open) => { if (!open) setEditSaleTarget(null); }}
                onSubmit={(form) => {
                    if (editSaleTarget) {
                        editSaleMutation.mutate({
                            saleId: editSaleTarget.sale.saleId,
                            buyerId: editSaleTarget.sale.buyerId,
                            form,
                        });
                    }
                }}
                isPending={editSaleMutation.isPending}
                initialData={editSaleTarget?.sale ?? null}
            />
        </Card>
    );
}
